import { Sprout, Boxes, Sparkles, ArrowUpRight } from 'lucide-react'
import type { ReactNode } from 'react'
import SectionHeading from '@/components/feature/SectionHeading'
import { useLanguage } from '@/localization'

interface ValueCard {
  icon: ReactNode
  title: string
  body: string
  accent: string
}

const VALUE_CARDS: ValueCard[] = [
  {
    icon: <Sprout size={26} />,
    title: 'Fair & friendly',
    body: 'Clear rules, active staff, friendly chat, and a server that stays fun for everyone.',
    accent: 'bg-grass-dark text-white',
  },
  {
    icon: <Boxes size={26} />,
    title: 'Pick your style',
    body: 'Survival or a curated Modded adventure — two worlds, the same welcoming crew.',
    accent: 'bg-sky-deep text-white',
  },
  {
    icon: <Sparkles size={26} />,
    title: 'Part of Uliunai',
    body: 'Backed by the long-running Uliunai gaming community. Minecraft is one server in a growing network.',
    accent: 'bg-gold text-slate-deep',
  },
]

/**
 * "Who we are / part of Uliunai" story block for the MC home. Mirrors the
 * cross-game community section (shared structure, grass-skinned): eyebrow +
 * heading + short story + a row of three value cards. Reuses MC's grass tokens
 * and the shared SectionHeading so it reads with the rest of the home.
 */
export default function CommunitySection() {
  const { t } = useLanguage()

  return (
    <section id="community" className="mx-auto w-full max-w-6xl px-4 py-16 sm:py-20">
      <SectionHeading
        eyebrow="Who we are"
        title="More than a server — a community"
        subtitle="Uliunai Minecraft is part of the wider Uliunai gaming community: the same people, values and care that have kept our servers running for years, now block by block."
      />

      <div className="mx-auto mb-12 max-w-3xl space-y-4 text-center text-lg text-slate-deep/75">
        <p>
          {t(
            'We started as a single game community and grew into a small network of servers. Minecraft is our latest home — built the same way as the rest: friendly first, fair always, and run by people who actually play here.',
          )}
        </p>
        <p>
          {t(
            'Whether you came for relaxed Survival or a deep Modded grind, you join one community with shared staff, shared rules and a shared Discord.',
          )}
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        {VALUE_CARDS.map((c) => (
          <div
            key={c.title}
            className="rounded-2xl border border-stone-light/60 bg-white p-7 text-center shadow-block transition-transform hover:-translate-y-1"
          >
            <div
              className={`mx-auto mb-4 inline-flex h-14 w-14 items-center justify-center rounded-xl shadow-block ${c.accent}`}
            >
              {c.icon}
            </div>
            <h3 className="mb-2 text-xl font-extrabold text-slate-deep">{t(c.title)}</h3>
            <p className="text-slate-deep/70">{t(c.body)}</p>
          </div>
        ))}
      </div>

      <div className="mt-10 text-center">
        <a
          href="https://uliunai.com"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 font-bold text-grass-deep underline-offset-4 transition-colors hover:text-grass-dark hover:underline"
        >
          {t('Explore the whole Uliunai community')} <ArrowUpRight size={16} />
        </a>
      </div>
    </section>
  )
}
